import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, FolderOpen } from "lucide-react";
import PageShell from "../components/PageShell";
import { categories, getCategoryPreview } from "../data/projects";

const Portfolio = () => {
  return (
    <PageShell testId="page-portfolio">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-8 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#A8D2FA] mb-4">
            Portfolio · 2026
          </div>
          <h1 className="font-fraunces text-5xl md:text-7xl font-light text-slate-900 dark:text-slate-100 leading-[1.05] max-w-4xl text-balance">
            Series &amp; sessions, <em className="italic">gathered</em> together.
          </h1>
          <p className="mt-6 font-manrope text-slate-600 dark:text-slate-300 max-w-2xl leading-relaxed">
            Each folder below opens a small archive — the projects I&apos;ve
            shot, added to over time.
          </p>
        </motion.div>
      </section>

      <section
        className="max-w-7xl mx-auto px-6 md:px-12 pb-24"
        data-testid="portfolio-categories"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {categories.map((c, i) => (
            <CategoryFolderCard key={c.slug} category={c} index={i} />
          ))}
        </div>
      </section>
    </PageShell>
  );
};

const CategoryFolderCard = ({ category, index }) => {
  const preview = getCategoryPreview(category.slug);
  const cover = preview.cover;
  const objectPos = preview.projects[0]?.heroObjectPosition || "";
  const count = preview.count;
  const offset = index % 2 === 0 ? "md:mt-0" : "md:mt-16";
  return (
    <Link
      to={`/portfolio/category/${category.slug}`}
      data-testid={`category-card-${category.slug}`}
      className={`group block ${offset}`}
    >
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="relative overflow-hidden rounded-[32px] border border-white/70 shadow-[0_10px_40px_rgba(198,226,255,0.25)] bg-gradient-to-br from-[#E8F4FF]/60 via-white/70 to-[#F4FAFF]/60 aspect-[4/5]"
      >
        {cover ? (
          <img
            src={cover}
            alt={`${category.label} — cover`}
            loading="lazy"
            className={`w-full h-full object-cover ${objectPos} transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FolderOpen size={40} className="text-[#A8D2FA]" />
          </div>
        )}

        {/* Soft top gradient for label legibility */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black/25 to-transparent" />

        {/* Count chip */}
        <div className="absolute top-5 right-5">
          <span className="glass-strong rounded-full px-3.5 py-1.5 font-manrope text-[10px] tracking-[0.3em] uppercase text-slate-800 dark:text-slate-200">
            {count} {count === 1 ? "project" : "projects"}
          </span>
        </div>

        {/* Folder label */}
        <div className="absolute top-5 left-5 inline-flex items-center gap-2 glass rounded-full px-3.5 py-1.5">
          <FolderOpen size={12} className="text-slate-700 dark:text-slate-200" />
          <span className="font-manrope text-[10px] tracking-[0.3em] uppercase text-slate-800 dark:text-slate-200">
            Folder
          </span>
        </div>
      </motion.div>

      <div className="mt-6 flex items-start justify-between gap-4">
        <div>
          <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-slate-400 dark:text-slate-500">
            Category
          </div>
          <h3 className="font-fraunces text-2xl md:text-3xl font-light text-slate-900 dark:text-slate-100 mt-2 group-hover:text-slate-700 dark:text-slate-200 transition">
            {category.label}
          </h3>
          <p className="mt-3 font-manrope text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 leading-relaxed max-w-md">
            {category.description}
          </p>
        </div>
        <div className="glass rounded-full p-2.5 mt-1 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1">
          <ArrowUpRight size={16} className="text-slate-700 dark:text-slate-200" />
        </div>
      </div>
    </Link>
  );
};

export default Portfolio;
